import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useId, useMemo } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import { useAuth } from '../context/AuthContext';
import { addProjectNote } from '../graphqlService';
import { brand, neutral } from '../theme';
import { formatDateTime, formatRelativeTime } from '../utils/format';
import EmptyState from './ui/EmptyState';
import { readableError } from './ProjectWorkspace/reviewModel';

interface ProjectNote {
  id: string;
  message: string;
  authorId: string;
  authorName: string;
  authorRole: string;
  timestamp: string;
  type: 'GENERAL' | 'REVISION_REQUEST' | 'CLARIFICATION' | 'SYSTEM';
}

/**
 * Types a person can choose when writing a note. SYSTEM is reserved for notes
 * the platform writes itself (status changes and the like); offering it in the
 * picker let anyone post a message dressed up as an automated one.
 */
const SELECTABLE_NOTE_TYPES = ['GENERAL', 'CLARIFICATION', 'REVISION_REQUEST'] as const;
type SelectableNoteType = typeof SELECTABLE_NOTE_TYPES[number];

const isSelectableNoteType = (value: unknown): value is SelectableNoteType =>
  (SELECTABLE_NOTE_TYPES as ReadonlyArray<unknown>).includes(value);

/** Picker labels, and the tag shown on a message (General messages carry none). */
const NOTE_TYPE_LABELS: Record<ProjectNote['type'], string> = {
  GENERAL: 'General',
  CLARIFICATION: 'Question',
  REVISION_REQUEST: 'Change request',
  SYSTEM: 'System',
};

const ROLE_LABELS: Record<string, string> = {
  creator: 'Creator',
  editor: 'Editor',
  admin: 'PithPlay team',
  superadmin: 'PithPlay team',
  system: 'PithPlay',
};

interface ProjectNotesProps {
  projectId: string;
  notes?: ProjectNote[];
  onNoteAdded?: () => void;
  /** Layout overrides for the panel, e.g. a height-limited side panel. */
  sx?: SxProps<Theme>;
}

export interface ProjectNotesRef {
  focusNoteById: (id: string) => void;
  focusNoteByTime: (time: number) => void;
}

/**
 * Seconds -> MM:SS (or H:MM:SS). Notes have no timeline field, so a timecode
 * prefix in the body is how a note gets anchored to a moment in the cut.
 */
const formatStamp = (seconds: number): string => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hrs > 0 ? `${hrs}:${pad(mins)}:${pad(secs)}` : `${pad(mins)}:${pad(secs)}`;
};

const initialsOf = (name: string) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

const roleLabel = (role?: string | null) => {
  const key = String(role || '').trim().toLowerCase();
  if (!key) return '';
  return ROLE_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
};

const isMacPlatform = () => {
  if (typeof navigator === 'undefined') return false;
  return /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent || '');
};

/**
 * Messages between the creator and the editor about a project. The composer
 * sits above the list (newest first), so it stays in view however long the
 * conversation gets. Shared by the creator's project page and the editor
 * workspace.
 */
const ProjectNotes = forwardRef<ProjectNotesRef, ProjectNotesProps>(({ projectId, notes = [], onNoteAdded, sx }, ref) => {
  const [newMessage, setNewMessage] = useState('');
  const [noteType, setNoteType] = useState<SelectableNoteType>('GENERAL');
  const [loading, setLoading] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [highlightedNoteId, setHighlightedNoteId] = useState<string | null>(null);
  const { user, getToken } = useAuth();
  const listRef = useRef<HTMLDivElement | null>(null);
  const noteRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const composerRef = useRef<HTMLInputElement | null>(null);
  const headingId = useId();
  const typeLabelId = useId();
  // The send shortcut, for keyboards only (not touch screens).
  const placeholder = useMemo(() => {
    const touch = typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)')?.matches);
    if (touch) return 'Write a message';
    return `Write a message (${isMacPlatform() ? '⌘' : 'Ctrl'} + Enter to send)`;
  }, []);

  // Deduplicate notes by ID, then sort by timestamp (newest first).
  const sortedNotes = useMemo(() => {
    const unique = Object.values(
      notes.reduce((acc: { [key: string]: ProjectNote }, note) => {
        if (note && note.id) acc[note.id] = note;
        return acc;
      }, {})
    );
    return unique.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [notes]);

  const highlight = (id: string) => {
    const noteElement = noteRefs.current[id];
    if (!noteElement) return false;
    setHighlightedNoteId(id);
    noteElement.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    setTimeout(() => setHighlightedNoteId(null), 3000);
    return true;
  };

  useImperativeHandle(ref, () => ({
    focusNoteById: (id: string) => {
      highlight(id);
    },
    focusNoteByTime: (time: number) => {
      // Notes carry no timeline position, so there is no note to jump TO.
      // Highlight a note already tagged with this timecode, and otherwise
      // seed the composer with the timecode so the reply is anchored to that
      // moment.
      const stamp = formatStamp(time);
      const existing = sortedNotes.find((note) => note.message?.startsWith(`[${stamp}]`));
      if (existing && highlight(existing.id)) return;

      setNewMessage((current) =>
        current.startsWith(`[${stamp}]`) ? current : `[${stamp}] ${current}`.trimEnd() + ' ',
      );
      composerRef.current?.focus();
      composerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    },
  }));

  const handleAddNote = async () => {
    if (!newMessage.trim() || !user || loading) return; // Prevent double submission

    setLoading(true);
    setSendError(null);
    try {
      const token = await getToken();
      if (!token) {
        setSendError('Your session has expired. Sign in again to send messages.');
        return;
      }
      await addProjectNote(projectId, newMessage.trim(), noteType, token);
      setNewMessage('');
      setNoteType('GENERAL');
      onNoteAdded?.();
    } catch (error) {
      console.error('Error adding note:', error);
      setSendError(readableError(error, 'Your message wasn’t sent. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !loading) {
      event.preventDefault();
      void handleAddNote();
    }
  };

  // Show the newest message when one arrives.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [sortedNotes.length]);

  return (
    <Card
      component="section"
      aria-labelledby={headingId}
      data-testid="project-notes"
      sx={[
        { display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, px: 2.5, py: 2, flexShrink: 0 }}>
        <Typography id={headingId} variant="h6" component="h2" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.4 }}>
          Messages{' '}
          <Box component="span" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {sortedNotes.length}
          </Box>
        </Typography>
      </Box>
      <Divider />

      <Box
        component="form"
        noValidate
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          void handleAddNote();
        }}
        sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.25, flexShrink: 0 }}
      >
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={8}
          placeholder={placeholder}
          inputProps={{ 'aria-label': 'Message' }}
          inputRef={composerRef}
          value={newMessage}
          onChange={(event) => setNewMessage(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
          <FormControl size="small" sx={{ minWidth: 156 }}>
            <InputLabel id={typeLabelId}>Type</InputLabel>
            <Select
              labelId={typeLabelId}
              value={noteType}
              label="Type"
              onChange={(event) => {
                if (isSelectableNoteType(event.target.value)) setNoteType(event.target.value);
              }}
            >
              {SELECTABLE_NOTE_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {NOTE_TYPE_LABELS[value]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            type="submit"
            variant="outlined"
            data-testid="project-notes-send"
            aria-label="Send message"
            disabled={!newMessage.trim() || loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
            sx={{ ml: 'auto' }}
          >
            Send
          </Button>
        </Box>
        {sendError && <Alert severity="error">{sendError}</Alert>}
      </Box>
      <Divider />

      <Box ref={listRef} sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {sortedNotes.length > 0 ? (
          <List disablePadding>
            {sortedNotes.map((note, index) => {
              const tag = note.type && note.type !== 'GENERAL' ? NOTE_TYPE_LABELS[note.type] ?? null : null;
              const role = roleLabel(note.authorRole);
              const when = formatRelativeTime(note.timestamp);
              return (
                <ListItem
                  key={note.id}
                  ref={(el) => {
                    noteRefs.current[note.id] = el;
                  }}
                  sx={{
                    display: 'block',
                    px: 2.5,
                    py: 1.75,
                    borderTop: index === 0 ? 'none' : `1px solid ${brand.border}`,
                    bgcolor: highlightedNoteId === note.id ? neutral[100] : 'transparent',
                    transition: 'background-color 0.3s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
                    <Avatar
                      aria-hidden="true"
                      sx={{ width: 28, height: 28, fontSize: '0.6875rem', fontWeight: 700, bgcolor: neutral[200], color: brand.slate }}
                    >
                      {initialsOf(note.authorName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {note.authorName || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="p">
                        {role}
                        {role && when ? ' · ' : ''}
                        {when && (
                          <Box component="time" dateTime={note.timestamp} title={formatDateTime(note.timestamp)}>
                            {when}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                    {tag && <Chip size="small" variant="outlined" label={tag} sx={{ flexShrink: 0 }} />}
                  </Box>
                  <Typography variant="body2" sx={{ pl: '38px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {note.message}
                  </Typography>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <EmptyState
            compact
            icon={<ChatBubbleOutlineOutlinedIcon />}
            title="No messages yet"
            description="Questions, answers and decisions about this project appear here."
          />
        )}
      </Box>
    </Card>
  );
});

ProjectNotes.displayName = 'ProjectNotes';

export default ProjectNotes;
